import { useMemo } from 'react';

import { Stack } from '../../../../Layout/Stack';
import classes from './Cart.module.css';
import { CartItem, CartItemProps } from './CartItem';
import { CartItemDescription } from './CartItemDescription';
import { CardCardCart } from './cartCard.types';
import { CartProps } from './types';
import { getCartItemIcon, typeToLinkMap, typeToTitleMap, useGetCartItemType } from './utils';

interface CartItemsStackProps {
  cartItems: CartProps['cartItems'];
  cartItemProps?: Omit<CartItemProps, 'onQuantityChange' | 'itemId'>;
  onQuantityChange?: ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) => void;
  actionsSize?: CartItemProps['actionsSize'];
  isRecurring?: boolean;
  cart: CardCardCart;
  promotions?: { onRemove: () => void; visible: boolean }[];
  showQuantitySelector?: boolean;
  showQuantityIndicator?: boolean;
  onClick?: (itemId: string) => void;
  onCartItemNameClick?: (link: string) => void;
  cause?: { shortener?: string; uuid: string };
}

const CartItemsStack = ({
  cartItems,
  cartItemProps,
  onQuantityChange,
  actionsSize,
  isRecurring,
  cart,
  cause,
  promotions,
  showQuantitySelector = true,
  showQuantityIndicator,
  onClick,
  onCartItemNameClick,
}: CartItemsStackProps) => {
  const getCartItemType = useGetCartItemType(cart);

  const cartItemsWithoutDiscounts = useMemo(() => {
    return cartItems.filter(
      (item) =>
        item.store_item?.type !== 'StoreItems::DiscountAmount' &&
        item.store_item?.type !== 'StoreItems::DiscountPercent'
    );
  }, [cartItems]);

  return (
    <Stack className={classes.items_container}>
      {cartItemsWithoutDiscounts?.map((item) => {
        const type = getCartItemType(item);
        const title = typeToTitleMap.get(type)?.(item);
        const icon = getCartItemIcon(type, item);
        const link = typeToLinkMap.get(type)?.(item, cause);

        const quantity = {
          value: item.quantity ?? 1,
          maxValue: type === 'membership' ? 1 : undefined,
        };

        const hasOptions = item.options && Object.keys(item.options).length > 0;

        const showItemQuantityIndicator =
          showQuantityIndicator && quantity.value > 1 && !hasOptions;

        return (
          <CartItem
            key={item.uuid}
            itemId={item.uuid}
            imgSrc={item.store_item?.images?.[0]?.url}
            name={title}
            onNameClick={
              link && onCartItemNameClick ? () => onCartItemNameClick?.(link) : undefined
            }
            total={Number(item.price)}
            quantity={quantity}
            actionsSize={actionsSize}
            withinPortal={false}
            showQuantitySelector={showQuantitySelector}
            showQuantityIndicator={showItemQuantityIndicator}
            variablePriced={item.store_item?.variable_price}
            onQuantityChange={onQuantityChange}
            onClick={onClick}
            description={
              <CartItemDescription cartItem={item} isRecurring={isRecurring} cart={cart} />
            }
            Icon={icon}
            iconProps={{
              color: type === 'pos' ? 'cobalt.1' : undefined,
              c: type === 'pos' ? 'cobalt.6' : undefined,
              size: '65px',
            }}
            {...cartItemProps}
          />
        );
      })}
    </Stack>
  );
};

export { CartItemsStack };
export type { CartItemsStackProps };
